<template>
    <div class="entity-panel" v-infinite-scroll="loadMore" :infinite-scroll-distance="16" :infinite-scroll-disabled="allLoaded || loading">
        <entity-row v-if="page || !loading"
                    v-for="row in rows"
                    :key="row.id"
                    :title="row.title"
                    :status="row.status"
                    :status-editable="isStatusEditable"
                    :tags="row.tags"
                    :url="getEntityUrl(row.id)"
                    :linked="isLinked(row.id)"
                    @toggle-link="toggleLink(row.id)"
                    @toggle-status="toggleStatus(row, $event)"/>
        <div :class="['loader', { 'full': !page }]" v-loading="loading" v-if="loading"></div>
        <div v-if="!rows.length && !loading" class="no-results">{{ $t('no_data') }}</div>
    </div>
</template>

<script lang="ts">
    import Vue, { PropType } from 'vue';

    import entityRow from './entity-row.vue';

    import { EntityProvider, EntityRow, EntityStatus, GenericEntityProvider } from '@/utilities/entities';
    import { EntityType } from '@/utilities/fields/metadata';
    import { NavigationProvider } from '@/utilities/navigation';
    import { EmailInfo, EmailProvider } from '@/utilities/api/email';

    export default Vue.extend({
        name: 'entity-panel',
        components: {
            entityRow
        },
        props: {
            entityType: Number as PropType<EntityType>,
            organisationId: Number,
            showAll: Boolean,
            email: Object as PropType<EmailInfo>
        },
        data() {
            return {
                loading: false,
                allLoaded: false,
                page: 0,
                entities: [] as any[]
            };
        },
        computed: {
            entityProvider(): GenericEntityProvider<any> {
                return EntityProvider.getProvider(this.entityType);
            },
            loadState(): string {
                return `${this.entityType}-${this.organisationId}-${this.showAll}`;
            },
            rows(): EntityRow[] {
                return this.entities.map(e => EntityProvider.getEntityRow(this.entityType, e, this.showAll));
            },
            isStatusEditable(): boolean {
                return EntityProvider.isStatusEditable(this.entityType);
            },
        },
        watch: {
            loadState: {
                immediate: true,
                handler() {
                    this.loadData();
                }
            }
        },
        methods: {
            async loadData(reset = true) {
                this.loading = true;

                if (reset) {
                    this.allLoaded = false;
                    this.page = 0;
                    this.entities = [];
                } else {
                    this.page++;
                }

                const entities = this.showAll
                    ? await this.entityProvider.getAllByOrganisation(this.organisationId, this.page)
                    : await this.entityProvider.getMyByOrganisation(this.organisationId, this.page);

                this.entities.push(...entities);
                await this.makeLinkedEntityFirst();
                this.allLoaded = entities.length < EntityProvider.DefaultPageSize;

                this.loading = false;
            },
            loadMore() {
                if (this.loading) return;
                this.loadData(false);
            },
            async makeLinkedEntityFirst() {
                if (!this.email || this.email.entityType != this.entityType || !this.email.entityId) return;
                if (this.page > 0) {
                    // Remove if linked entity appears on the next pages
                    const linkedIndex = this.rows.findIndex((r, i) => i > 0 && r.id == this.email.entityId);
                    if (linkedIndex >= 0) {
                        this.entities.splice(linkedIndex, 1);
                    }
                } else {
                    const linkedIndex = this.rows.findIndex(r => r.id == this.email.entityId);
                    if (linkedIndex >= 0) {
                        this.entities.unshift(this.entities.splice(linkedIndex, 1)[0]);
                    } else {
                        const linkedEntity = await this.entityProvider.get(this.email.entityId);
                        if (linkedEntity) {
                            this.entities.unshift(linkedEntity);
                        }
                    }
                }
            },
            getEntityUrl(id: number) {
                return NavigationProvider.getEntityUrl(this.entityType, id);
            },
            isLinked(id: number) {
                if (!this.email) return undefined;
                return this.email.entityType == this.entityType && this.email.entityId == id;
            },
            async toggleLink(id: number) {
                if (this.isLinked(id)) {
                    this.email.entityType = EntityType.Undefined;
                    this.email.entityId = 0;
                } else {
                    this.email.entityType = this.entityType;
                    this.email.entityId = id;
                }
                await EmailProvider.updateEntityLink(this.email);
            },
            async toggleStatus(row: EntityRow, callback: Function) {
                try {
                    const model = EntityProvider.getStatusChangeModel(this.entityType, row.id, row.status != EntityStatus.Completed);
                    await this.entityProvider.save(model);
                    const index = this.rows.indexOf(row);
                    const entity = await this.entityProvider.get(row.id);
                    if (entity) {
                        this.$set(this.entities, index, entity);
                    } else {
                        this.$set(this.entities[index], '$status', EntityStatus.Disabled);
                    }
                } catch (e: any) {
                    console.error(e);
                    this.$notify.error({ title: this.$tc('error'), message: e.error || this.$tc('an_unexpected_error_occurred') });
                }
                callback();
            }
        }
    });

</script>

<style scoped lang="scss">

    .entity-panel {
        display: flex;
        flex-direction: column;

        & > .entity-row:not(:last-child) {
            padding-bottom: 8px;
            border-bottom: solid 1px var(--grey-200);
        }

        & > .entity-row:not(:first-child) {
            padding-top: 8px;
        }

        .loader {
            position: relative;
            width: 100%;
            height: 64px;

            &.full {
                height: 300px;
            }
        }

        .no-results {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 200px;
            gap: 8px;
            @extend %body-16-medium;
            color: var(--grey-700);

            &::before {
                @extend %md-icon;
                content: 'search';
                font-size: 32px;
            }
        }
    }

</style>